import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const faqs = [
  { q: 'How much does shipping cost on AllChinaBuy?', a: 'Shipping costs depend on the destination country, package weight, and chosen shipping method. Economy lines are cheaper but slower (2-6 weeks), while express options like DHL and FedEx are faster (1-2 weeks) but more expensive. You can use the shipping calculator on the main website to estimate costs before placing an order. Consolidating multiple items into one package often reduces per-item shipping costs.' },
  { q: 'What shipping methods does AllChinaBuy offer?', a: 'AllChinaBuy typically offers EMS, DHL, FedEx, UPS, and various economy shipping lines including China Post, ePacket, and specialized lines for specific regions. Each method has different delivery times, tracking capabilities, and customs handling procedures. We recommend choosing based on your budget, urgency, and local customs experience.' },
  { q: 'How long does delivery take?', a: 'Delivery times vary by shipping method: Economy lines take 2-6 weeks, EMS takes 1-3 weeks, and express couriers (DHL/FedEx) take 1-2 weeks. These estimates do not include potential customs delays, which can vary by country and season. Holiday periods and peak shopping seasons may also extend delivery times.' },
  { q: 'Can I consolidate multiple orders for cheaper shipping?', a: 'Yes, AllChinaBuy allows package consolidation. Instead of shipping each item separately, you can store purchased items in the warehouse for free (usually up to 180 days) and ship them together. This reduces the total cost compared to shipping items individually, though the consolidated package weight may affect the shipping rate differently.' },
];

export default function ShippingPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">{t('nav.shipping')}</span>
          </nav>
        </div>
      </div>

      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            AllChinaBuy Shipping Guide
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Complete guide to AllChinaBuy shipping methods, costs, delivery times, package consolidation, customs, and tips for choosing the best option for your orders.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>Understanding AllChinaBuy Shipping</h2>
            <p>
              Shipping is one of the most important factors to consider when using AllChinaBuy or any international buying agent. After your items arrive at the AllChinaBuy warehouse and pass quality inspection, you will need to choose a shipping method to deliver your package to your country. The cost, speed, and reliability of this step can significantly impact your overall buying experience and total expenditure.
            </p>
            <p>
              This guide breaks down the available shipping options, explains how costs are calculated, and provides practical advice on choosing the best method for your specific situation. Whether you are shipping a single pair of sneakers or a large consolidated package of clothing and accessories, understanding shipping logistics will help you make informed decisions and avoid unexpected costs.
            </p>

            <h2>Shipping Methods Explained</h2>
            <p>
              AllChinaBuy offers several shipping tiers, each with different trade-offs between cost and speed:
            </p>
            <h3>Economy Shipping Lines</h3>
            <p>
              Economy lines such as China Post, ePacket, and various regional logistics providers offer the cheapest shipping rates but the longest delivery times, typically ranging from 2 to 6 weeks. These methods are ideal for non-urgent orders and buyers who prioritize cost savings over speed. Tracking quality varies by provider, and customs clearance can sometimes take longer with economy options.
            </p>
            <h3>Standard Express (EMS)</h3>
            <p>
              EMS is a middle-ground option that balances cost and speed. Delivery typically takes 1 to 3 weeks, and EMS provides reliable tracking throughout the journey. EMS is a popular choice for medium-value packages and is available to most countries. It is generally more reliable than economy lines for customs clearance, though this varies by destination country.
            </p>
            <h3>Premium Express (DHL, FedEx, UPS)</h3>
            <p>
              Premium courier services offer the fastest delivery, usually 1 to 2 weeks, with excellent tracking and customer service. However, they are the most expensive option and may be subject to higher customs scrutiny due to the commercial appearance of the shipment. For high-value items or time-sensitive orders, premium express is often worth the extra cost. Be aware that some countries may charge higher import duties on express shipments.
            </p>

            <h2>Shipping Cost Calculation</h2>
            <p>
              Shipping costs are primarily calculated based on the greater of actual weight or volumetric weight of your package. Volumetric weight is calculated by multiplying the package dimensions (length x width x height) and dividing by a standard divisor (usually 5000 or 6000). This means that large but lightweight packages (like a box of shoes) may cost more to ship than their actual weight suggests.
            </p>
            <p>
              Additional factors affecting cost include the destination country, chosen shipping method, and whether you purchase insurance or declare a specific value for customs. Some shipping methods offer flat-rate options for certain weight ranges, while others use per-kilogram pricing. We recommend using the shipping calculator on AllChinaBuy before submitting your package to get an accurate cost estimate.
            </p>

            <h2>Package Consolidation Strategy</h2>
            <p>
              One of the best ways to save on shipping is package consolidation. When you purchase multiple items over a period of time, AllChinaBuy can store them in their warehouse (usually free for up to 180 days) and ship them together in one package. This reduces the per-item shipping cost because you are paying for one shipment instead of multiple individual shipments.
            </p>
            <p>
              However, consolidation has a trade-off: the combined package weight and volume may push you into a higher shipping rate bracket. Additionally, larger consolidated packages may attract more customs attention. We recommend balancing consolidation benefits with the risk of higher duties and potential customs delays for your specific country.
            </p>

            <h2>Customs and Import Duties</h2>
            <p>
              When your package arrives in your country, it must pass through customs inspection. The import duties and taxes you pay depend on your country regulations, the declared value of the package, and the type of items shipped. Some countries have duty-free thresholds for low-value packages, while others charge duties on all imports regardless of value.
            </p>
            <p>
              AllChinaBuy allows you to declare the package value for customs purposes, but under-declaring can lead to penalties or confiscation if discovered. We recommend declaring a realistic value and researching your country specific import rules before shipping. For more safety tips, visit our <a href={`/${lang}/safety/`} className="text-primary-600 hover:underline">Safety Guide</a>.
            </p>

            <h2>Shipping Tips for Best Results</h2>
            <p>
              1. <strong>Choose shipping based on your priority</strong> - If you need items quickly, use express. If you are price-sensitive, use economy. For a balance, use EMS.
            </p>
            <p>
              2. <strong>Consolidate wisely</strong> - Combine orders to save on shipping, but be mindful of package size limits and customs thresholds.
            </p>
            <p>
              3. <strong>Remove packaging when possible</strong> - Request removal of unnecessary shoe boxes and tags to reduce volumetric weight.
            </p>
            <p>
              4. <strong>Research your country rules</strong> - Know your local customs thresholds and prohibited items to avoid confiscation.
            </p>
            <p>
              5. <strong>Always use tracking</strong> - Choose shipping methods that offer end-to-end tracking so you can monitor your package progress and respond to issues promptly.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main max-w-3xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            Shipping FAQ
          </h2>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-background-200/60 rounded-xl bg-background-50 overflow-hidden" itemScope itemType="https://schema.org/Question">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                >
                  <span className="font-medium text-foreground-900 text-sm md:text-base" itemProp="name">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-lg text-primary-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5" itemScope itemType="https://schema.org/Answer">
                    <p className="text-foreground-600 text-sm leading-relaxed" itemProp="text">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-background-200/70 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Ready to Start Shopping?
            </h3>
            <p className="text-foreground-600 mb-6 leading-relaxed">
              Browse products in the AllChinaBuy spreadsheet, add them to your cart, and plan your shipping strategy for the best value.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`/${lang}/products/`} className="btn-primary inline-flex items-center justify-center">
                View Products <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="btn-secondary inline-flex items-center justify-center">
                Buy It Now <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}